import React, { useState, useEffect } from 'react'
import ExcelJS from 'exceljs'
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Snackbar,
} from '@mui/material'
import { DataGrid, GridToolbar } from '@mui/x-data-grid'
import RefreshIcon from '@mui/icons-material/Refresh'
import DownloadIcon from '@mui/icons-material/Download'
import CardMembershipIcon from '@mui/icons-material/CardMembership'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import { certificateApi } from '../utils/api'

// FastAPI/Pydantic v2 returns detail as an array of {type,loc,msg,input,ctx,url} objects.
// This helper always produces a plain string safe to render as a React child.
const extractErrorMessage = (detail, fallback) => {
  if (!detail) return fallback
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    return detail.map(e => (e.msg ? `${e.loc?.slice(-1)[0] ?? 'field'}: ${e.msg}` : JSON.stringify(e))).join('; ')
  }
  if (typeof detail === 'object') return detail.msg ?? JSON.stringify(detail)
  return fallback
}

const AWSCertificateTable = () => {
  const [certificates, setCertificates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  })
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingCert, setEditingCert] = useState(null)
  const [sanEntries, setSanEntries] = useState([{ value: '', readOnly: false }])
  const [saveLoading, setSaveLoading] = useState(false)
  const [sanScheduledDate, setSanScheduledDate] = useState('')   // for deferred CRQ SAN updates
  const [sanCreatedCrq, setSanCreatedCrq] = useState('')         // CRQ number returned after save
  const [saveError, setSaveError] = useState(null)
  const [snackbarOpen, setSnackbarOpen] = useState(false)

  useEffect(() => {
    fetchCertificates()
  }, [])

  const fetchCertificates = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await certificateApi.getAll({
        skip: 0,
        limit: 10000,
      })
      
      // Show both on-prem and AWS certificates
      let filteredCerts = response.data.items.map(cert => ({
        id: cert.cert_id,
        commonName: cert.common_name,
        sanEntries: cert.subject_alt_names
          ? cert.subject_alt_names.split(',').map(s => s.trim()).filter(Boolean).join(', ')
          : '—',
        accountName: cert.aws_account === 'ONPREM' 
          ? 'On-Premises' 
          : (cert.account_name || `aws-${cert.product_name}-account`),
        accountNumber: cert.aws_account,
        renewalStatus: cert.renewal_status || 'N/A',
        renewalCRQ: cert.renewal_crq || '',
        layer7CRQ: cert.layer7_crq || '',
        acmUsage: cert.acm_usage ? 'Yes' : 'No',
        productName: cert.product_name || cert.product_code,
        expiryDate: cert.expiration_date ? new Date(cert.expiration_date).toLocaleDateString() : 'N/A',
        environment: cert.sdlc || 'N/A',
        certType: cert.cert_type || 'N/A',
        layer7: cert.layer7 ? 'Yes' : 'No',
        clientInvolvement: cert.client_involvement ? 'Yes' : 'No',
        dependencyPresent: cert.dependency_present ? 'Yes' : 'No',
        updateRequired: cert.requires_update ? 'Yes' : 'No',
        status: formatStatus(cert.status),
        certificateType: cert.aws_account === 'ONPREM' ? 'On-Prem' : 'AWS',
      }))

      setCertificates(filteredCerts)
    } catch (err) {
      console.error('Error fetching certificates:', err)
      setError(extractErrorMessage(err.response?.data?.detail, 'Failed to load certificates. Please try again.'))
    } finally {
      setLoading(false)
    }
  }

  const deriveEnvironment = (accountNum) => {
    // This is a simple heuristic - adjust based on your actual logic
    const lastDigit = parseInt(accountNum.slice(-1))
    if (lastDigit <= 2) return 'dev'
    if (lastDigit <= 5) return 'qa'
    if (lastDigit <= 7) return 'uat'
    return 'prd'
  }

  const formatStatus = (status) => {
    const statusMap = {
      'ACTIVE': 'Active',
      'EXPIRING_SOON': 'Expiring Soon',
      'EXPIRED': 'Expired',
      'PENDING': 'Pending',
      'ONBOARDED': 'Onboarded',
      'REVOKED': 'Revoked',
    }
    return statusMap[status] || status
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
      case 'Onboarded':
        return 'success'
      case 'Expiring Soon':
        return 'warning'
      case 'Expired':
      case 'Revoked':
        return 'error'
      case 'Pending':
        return 'info'
      default:
        return 'default'
    }
  }

  const renderCellWithWrap = (params) => {
    const value = params.value || ''
    const shouldWrap = value.length > 40
    
    return (
      <Box
        sx={{
          whiteSpace: shouldWrap ? 'normal' : 'nowrap',
          wordWrap: shouldWrap ? 'break-word' : 'normal',
          lineHeight: '1.5',
          padding: '8px 0',
        }}
      >
        {value}
      </Box>
    )
  }

  const renderProductCell = (params) => {
    const value = params.value || ''
    const shouldWrap = value.length > 50
    
    return (
      <Box
        sx={{
          whiteSpace: shouldWrap ? 'normal' : 'nowrap',
          wordWrap: shouldWrap ? 'break-word' : 'normal',
          wordBreak: shouldWrap ? 'break-word' : 'normal',
          lineHeight: '1.5',
          padding: '8px 0',
          width: '100%',
          overflow: 'hidden',
          textOverflow: shouldWrap ? 'clip' : 'ellipsis',
        }}
      >
        {value}
      </Box>
    )
  }

  const columns = [
    {
      field: 'certificateType',
      headerName: 'Type',
      minWidth: 100,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          color={params.value === 'AWS' ? 'primary' : 'secondary'}
        />
      ),
    },
    {
      field: 'commonName',
      headerName: 'Common Name',
      minWidth: 250,
      flex: 1,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'sanEntries',
      headerName: 'SAN Entries',
      minWidth: 250,
      flex: 1.5,
      renderCell: (params) => {
        if (!params.value || params.value === '—') return <span style={{ color: '#aaa' }}>—</span>
        const entries = params.value.split(',').map(s => s.trim()).filter(Boolean)
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', py: '6px', gap: '2px', width: '100%' }}>
            {entries.map((san, i) => (
              <Box key={i} sx={{ fontSize: '0.875rem', color: '#1e293b', lineHeight: 1.5, whiteSpace: 'nowrap' }}>
                {san}
              </Box>
            ))}
          </Box>
        )
      },
    },
    {
      field: 'productName',
      headerName: 'Product',
      width: 200,
      renderCell: renderProductCell,
    },
    {
      field: 'accountName',
      headerName: 'AWS Account',
      minWidth: 200,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'accountNumber',
      headerName: 'Account #',
      minWidth: 130,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 120,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          color={getStatusColor(params.value)}
        />
      ),
    },
    {
      field: 'renewalStatus',
      headerName: 'Renewal',
      minWidth: 100,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'renewalCRQ',
      headerName: 'Renewal CRQ',
      minWidth: 120,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'layer7CRQ',
      headerName: 'Layer7 CRQ',
      minWidth: 120,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'acmUsage',
      headerName: 'ACM',
      minWidth: 80,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'expiryDate',
      headerName: 'Expiry Date',
      minWidth: 110,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'environment',
      headerName: 'SDLC',
      minWidth: 90,
      renderCell: (params) => (
        <Chip 
          label={params.value.toUpperCase()} 
          size="small" 
          color={params.value === 'prd' ? 'error' : 'default'}
        />
      ),
    },
    {
      field: 'certType',
      headerName: 'Cert Type',
      minWidth: 100,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'layer7',
      headerName: 'Layer7',
      minWidth: 80,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'clientInvolvement',
      headerName: 'Client',
      minWidth: 80,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'dependencyPresent',
      headerName: 'Dependency',
      minWidth: 110,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'updateRequired',
      headerName: 'Update Req.',
      minWidth: 110,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      minWidth: 90,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="Edit SAN Entries">
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleEditSAN(params.row)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  const handleExport = async () => {
    const wb = new ExcelJS.Workbook()
    const ws = wb.addWorksheet('Certificates')

    ws.columns = [
      { header: 'Type',            key: 'certificateType', width: 10 },
      { header: 'Common Name',     key: 'commonName',      width: 45 },
      { header: 'SAN Entries',     key: 'sanEntries',      width: 40 },
      { header: 'Product',         key: 'productName',     width: 35 },
      { header: 'AWS Account',     key: 'accountName',     width: 25 },
      { header: 'Account #',       key: 'accountNumber',   width: 18 },
      { header: 'Status',          key: 'status',          width: 15 },
      { header: 'SDLC',            key: 'environment',     width: 8  },
      { header: 'Expiry Date',     key: 'expiryDate',      width: 14 },
      { header: 'Renewal',         key: 'renewalStatus',   width: 12 },
      { header: 'Renewal CRQ',     key: 'renewalCRQ',      width: 18 },
      { header: 'Layer7 CRQ',      key: 'layer7CRQ',       width: 18 },
      { header: 'ACM',             key: 'acmUsage',        width: 8  },
      { header: 'Cert Type',       key: 'certType',        width: 12 },
      { header: 'Layer7',          key: 'layer7',          width: 8  },
      { header: 'Client',          key: 'clientInvolvement', width: 8 },
      { header: 'Notes Present',   key: 'dependencyPresent', width: 14 },
      { header: 'Update Req.',     key: 'updateRequired',  width: 12 },
    ]

    // Bold header row
    ws.getRow(1).font = { bold: true }

    // All certificates regardless of active grid filters
    certificates.forEach(row => ws.addRow(row))

    const buffer = await wb.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `certificates_${new Date().toISOString().slice(0, 10)}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleEditSAN = (row) => {
    setEditingCert(row)
    const raw = row.sanEntries === '—' ? '' : row.sanEntries
    const existing = raw
      ? raw.split(',').map((s) => s.trim()).filter(Boolean)
      : []
    // Existing SANs: read-only text, deletable. Start with empty new entry if none exist.
    setSanEntries(
      existing.length > 0
        ? existing.map((v) => ({ value: v, readOnly: true }))
        : [{ value: '', readOnly: false }]
    )
    setSanScheduledDate('')
    setSanCreatedCrq('')
    setSaveError(null)
    setEditDialogOpen(true)
  }

  const handleSaveSAN = async () => {
    if (!editingCert) return

    // All SAN changes on regulated environments require a CRQ
    const _CRQ_ENVS = ['qa', 'uat', 'prd']
    const isRegulatedEnv = _CRQ_ENVS.includes((editingCert.environment || '').toLowerCase())
    const needsCrq = isRegulatedEnv

    if (needsCrq && !sanScheduledDate) {
      setSaveError('Please select a scheduled date for the CRQ.')
      return
    }

    setSaveLoading(true)
    setSaveError(null)
    try {
      const normalized = sanEntries.map((e) => e.value.trim()).filter(Boolean).join(',')
      const payload = { subject_alt_names: normalized }
      if (needsCrq) payload.san_scheduled_date = sanScheduledDate

      const response = await certificateApi.update(editingCert.id, payload)
      const crqNumber = response?.data?.san_update_crq || ''
      setSanCreatedCrq(crqNumber)
      setEditDialogOpen(false)
      setSnackbarOpen(true)
      fetchCertificates()
    } catch (err) {
      setSaveError(extractErrorMessage(err.response?.data?.detail, 'Failed to update SAN entries. Please try again.'))
    } finally {
      setSaveLoading(false)
    }
  }

  const handleSanChange = (index, value) => {
    setSanEntries((prev) =>
      prev.map((e, i) => (i === index && !e.readOnly ? { ...e, value } : e))
    )
  }

  const handleAddSanEntry = () => {
    setSanEntries((prev) =>
      prev.length < 5 ? [...prev, { value: '', readOnly: false }] : prev
    )
  }

  const handleRemoveSanEntry = (index) => {
    setSanEntries((prev) => {
      const updated = prev.filter((_, i) => i !== index)
      return updated.length > 0 ? updated : [{ value: '', readOnly: false }]
    })
  }

  const handleEditDialogClose = () => {
    if (saveLoading) return
    setEditDialogOpen(false)
    setEditingCert(null)
    setSanEntries([{ value: '', readOnly: false }])
    setSanScheduledDate('')
    setSanCreatedCrq('')
    setSaveError(null)
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CardMembershipIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Certificates
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchCertificates} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export">
            <IconButton onClick={handleExport} color="primary">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ height: 500, width: '100%' }}>
          <DataGrid
            rows={certificates}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            // MUI DataGrid (MIT/Community) hard-caps pageSize at 100; higher values throw at runtime.
            pageSizeOptions={[25, 50, 100]}
            paginationMode="client"
            checkboxSelection
            disableSelectionOnClick
            loading={loading}
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 300 },
              },
            }}
            getRowHeight={() => 'auto'}
            sx={{
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
                paddingTop: '8px',
                paddingBottom: '8px',
              },
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'normal',
                lineHeight: '1.2',
                fontWeight: 'bold',
              },
            }}
          />
        </Box>
      </Paper>

      {/* Edit SAN Entries Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit SAN Entries</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Certificate: <strong>{editingCert?.commonName}</strong>
          </Typography>
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {sanEntries.map((entry, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={`SAN Entry ${index + 1}`}
                  value={entry.value}
                  onChange={entry.readOnly ? undefined : (e) => handleSanChange(index, e.target.value)}
                  disabled={saveLoading}
                  placeholder={entry.readOnly ? undefined : 'e.g. example.com'}
                  inputProps={{ readOnly: entry.readOnly }}
                />
                <Tooltip title="Remove entry">
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveSanEntry(index)}
                      disabled={saveLoading}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddSanEntry}
              disabled={saveLoading || sanEntries.length >= 5}
              size="small"
              sx={{ alignSelf: 'flex-start', mt: 0.5 }}
            >
              Add Entry
            </Button>

            {/* CRQ date picker — shown for all SAN changes on regulated environments */}
            {(() => {
              const _CRQ_ENVS = ['qa', 'uat', 'prd']
              const isRegulatedEnv = _CRQ_ENVS.includes(
                (editingCert?.environment || '').toLowerCase()
              )
              if (!isRegulatedEnv) return null
              return (
                <Box sx={{ mt: 1 }}>
                  <Alert severity="info" sx={{ mb: 1.5 }}>
                    SAN updates require a CRQ for this environment.
                    The Venafi update will run automatically on the scheduled date.
                  </Alert>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="CRQ Scheduled Date"
                    value={sanScheduledDate}
                    onChange={(e) => setSanScheduledDate(e.target.value)}
                    disabled={saveLoading}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: new Date().toISOString().split('T')[0] }}
                    required
                  />
                </Box>
              )
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditDialogClose} disabled={saveLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveSAN}
            disabled={saveLoading}
            startIcon={saveLoading ? <CircularProgress size={16} /> : null}
          >
            {saveLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          {sanCreatedCrq
            ? `CRQ ${sanCreatedCrq} created. SAN update will be applied on the scheduled date.`
            : 'SAN entries updated successfully.'}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default AWSCertificateTable
